import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["cuitSelect", "addButton", "searchSection", "cuitInput", "searchButton", "searchResult", "statusMessage", "confirmationPanel"]
  static values = {
    patientId: Number,
    initialCuit: String,
    initialOrder: String,
    colppyClientId: String,
    colppyClientInfo: Object,
    savedCuits: Array
  }

  connect() {
    this.availableCuits = []
    this.clientConfirmed = false
    this.pendingClient = null

    // Cargar CUITs guardados previamente
    if (this.savedCuitsValue && this.savedCuitsValue.length > 0) {
      this.savedCuitsValue.forEach(savedCuit => {
        // Solo agregar si no es el CUIT del paciente
        if (savedCuit.cuit !== this.initialCuitValue) {
          this.availableCuits.push({
            cuit: savedCuit.cuit,
            name: savedCuit.razon_social || savedCuit.nombre_fantasia || "Cliente",
            colppyClientId: savedCuit.colppy_client_id || null,
            foundInColppy: !!savedCuit.colppy_client_id,
            saved: true
          })
        }
      })
    }

    // Si hay CUIT inicial del paciente, agregarlo a la lista
    if (this.initialCuitValue) {
      this.availableCuits.push({
        cuit: this.initialCuitValue,
        name: "Paciente",
        colppyClientId: this.colppyClientIdValue || null,
        foundInColppy: !!this.colppyClientIdValue
      })

      // Si hay información del cliente de Colppy, mostrar panel de confirmación
      if (this.colppyClientInfoValue && this.colppyClientInfoValue.id) {
        this.showConfirmationPanel(this.colppyClientInfoValue)
      } else if (this.colppyClientIdValue) {
        // Ya tiene un colppy_client_id guardado (fue verificado previamente), auto-confirmar
        this.clientConfirmed = true
        this.updateStatusMessage(this.colppyClientIdValue, true)
        // Despachar evento de confirmación con setTimeout para que los listeners estén listos
        setTimeout(() => this.dispatch("confirmed", { detail: { clientId: this.colppyClientIdValue, cuit: this.initialCuitValue } }), 0)
      } else if (this.initialCuitValue) {
        // Buscar automáticamente en Colppy si no tenemos el ID
        this.searchInColppy(this.initialCuitValue, true)
      }
    }

  }

  toggleSearch() {
    const isVisible = this.searchSectionTarget.style.display !== "none"
    this.searchSectionTarget.style.display = isVisible ? "none" : "block"

    if (!isVisible) {
      this.cuitInputTarget.focus()
    }
  }

  handleKeypress(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.search()
    }
  }

  async search() {
    const cuit = this.cuitInputTarget.value.trim()

    if (!cuit) {
      this.showSearchError("Por favor ingrese un CUIT")
      return
    }

    // Validar formato básico de CUIT (XX-XXXXXXXX-X)
    const cuitRegex = /^\d{2}-\d{8}-\d{1}$/
    if (!cuitRegex.test(cuit)) {
      this.showSearchError("Formato de CUIT inválido. Use el formato: XX-XXXXXXXX-X")
      return
    }

    await this.searchInColppy(cuit, false)
  }

  async searchByClientId(clientId, autoSelect = false) {
    // Deshabilitar botón mientras busca
    this.searchButtonTarget.disabled = true
    this.searchButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Buscando...'
    this.searchResultTarget.style.display = "none"

    try {
      const response = await fetch(`/patients/${this.patientIdValue}/invoices_and_payments/search_colppy_client?client_id=${encodeURIComponent(clientId)}`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const data = await response.json()

      if (data.success) {
        this.showSearchSuccess(data.client, data.client.cuit, autoSelect)
      } else {
        this.showSearchError(data.error || "Cliente no encontrado en Colppy")
      }
    } catch (error) {
      console.error("Error buscando cliente en Colppy:", error)
      this.showSearchError("Error al buscar cliente en Colppy. Por favor intente nuevamente.")
    } finally {
      // Rehabilitar botón
      this.searchButtonTarget.disabled = false
      this.searchButtonTarget.innerHTML = '<i class="fas fa-search"></i> Buscar'
    }
  }

  async searchInColppy(cuit, autoSelect = false) {
    // Deshabilitar botón mientras busca
    this.searchButtonTarget.disabled = true
    this.searchButtonTarget.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Buscando...'
    this.searchResultTarget.style.display = "none"

    try {
      const response = await fetch(`/patients/${this.patientIdValue}/invoices_and_payments/search_colppy_client?cuit=${encodeURIComponent(cuit)}`, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.success) {
        this.showSearchSuccess(data.client, cuit, autoSelect)
      } else {
        this.showSearchError(data.error || "Cliente no encontrado en Colppy")
        // Si no se encuentra, aún permitir agregar el CUIT
        if (!autoSelect) {
          this.showAddCuitOption(cuit, null, false)
        }
      }
    } catch (error) {
      console.error("Error buscando cliente en Colppy:", error)
      this.showSearchError("Error al buscar cliente en Colppy. Por favor intente nuevamente.")
    } finally {
      // Rehabilitar botón
      this.searchButtonTarget.disabled = false
      this.searchButtonTarget.innerHTML = '<i class="fas fa-search"></i> Buscar'
    }
  }

  showSearchSuccess(client, cuit, autoSelect = false) {
    // Siempre mostrar el panel de confirmación en lugar de auto-aceptar
    this.showConfirmationPanel(client)

    // Ocultar el searchResult ya que usamos confirmationPanel
    this.searchResultTarget.style.display = "none"
  }

  showConfirmationPanel(client) {
    this.pendingClient = client
    this.clientConfirmed = false

    const ivaConditionOptions = [
      { value: '1', label: 'Responsable Inscripto' },
      { value: '2', label: 'Exento' },
      { value: '3', label: 'Consumidor Final' },
      { value: '4', label: 'Monotributista' }
    ]
    const rawIva = String(client.id_condicion_iva || '0')
    const hasValidIva = ivaConditionOptions.some(opt => opt.value === rawIva)
    // If current value is "0" or unknown, default to Consumidor Final (3)
    const currentIva = hasValidIva ? rawIva : '3'
    const ivaSelectHtml = ivaConditionOptions.map(opt =>
      `<option value="${opt.value}" ${opt.value === currentIva ? 'selected' : ''}>${opt.label}</option>`
    ).join('')
    const ivaWarning = !hasValidIva ? ' <span class="badge badge-warning">Valor original: ' + rawIva + ' (inválido)</span>' : ''

    this.confirmationPanelTarget.innerHTML = `
      <div class="card border-info">
        <div class="card-header bg-info text-white py-2">
          <i class="fas fa-check-circle"></i> <strong>Cliente encontrado en Colppy</strong>
        </div>
        <div class="card-body py-2">
          <table class="table table-sm table-borderless mb-2">
            <tbody>
              <tr>
                <td class="text-muted" style="width: 140px;">CUIT:</td>
                <td><strong>${client.cuit || 'N/A'}</strong></td>
              </tr>
              <tr>
                <td class="text-muted">Razón Social:</td>
                <td><strong>${client.razon_social || 'N/A'}</strong></td>
              </tr>
              <tr>
                <td class="text-muted">Nombre Fantasía:</td>
                <td>${client.nombre_fantasia || 'N/A'}</td>
              </tr>
              <tr>
                <td class="text-muted">Condición IVA:</td>
                <td>
                  <select id="confirmation-iva-condition" class="form-control form-control-sm" style="display: inline-block; width: auto;">
                    ${ivaSelectHtml}
                  </select>
                  ${ivaWarning}
                </td>
              </tr>
              <tr>
                <td class="text-muted">ID Colppy:</td>
                <td>${client.id || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
          <div class="d-flex">
            <button type="button" class="btn btn-sm btn-success mr-2" data-action="click->cuit-selector#confirmClient">
              <i class="fas fa-check"></i> Confirmar datos
            </button>
            <button type="button" class="btn btn-sm btn-outline-danger" data-action="click->cuit-selector#rejectClient">
              <i class="fas fa-times"></i> Rechazar
            </button>
          </div>
        </div>
      </div>
    `
    this.confirmationPanelTarget.style.display = "block"

    // Actualizar status message para indicar que hay confirmación pendiente
    this.statusMessageTarget.innerHTML = `<i class="fas fa-hourglass-half text-warning"></i> Cliente encontrado en Colppy - <strong>Pendiente de confirmación</strong>`

    // Notificar al invoice controller que hay confirmación pendiente
    this.dispatch("pending", { detail: { client: client } })
  }

  confirmClient() {
    if (!this.pendingClient) return

    const client = this.pendingClient
    this.clientConfirmed = true

    // Read the operator-selected IVA condition from the confirmation panel dropdown
    const ivaSelect = this.confirmationPanelTarget.querySelector('#confirmation-iva-condition')
    const selectedIvaConditionId = ivaSelect ? ivaSelect.value : null

    // Agregar el CUIT al selector
    this.addCuitToSelector(client.cuit, client.id, client.razon_social || client.nombre_fantasia, true)

    // Actualizar status message para mostrar estado confirmado
    this.statusMessageTarget.innerHTML = `<i class="fas fa-check-circle text-success"></i> Cliente confirmado en Colppy: <strong>${client.razon_social || client.nombre_fantasia || 'Cliente'}</strong> (ID: ${client.id})`

    // Ocultar panel de confirmación
    this.confirmationPanelTarget.style.display = "none"
    this.confirmationPanelTarget.innerHTML = ""

    // Limpiar pendingClient
    this.pendingClient = null

    // Despachar evento para que el invoice controller habilite el botón y sincronice IVA
    this.dispatch("confirmed", { detail: { clientId: client.id, cuit: client.cuit, ivaConditionId: selectedIvaConditionId } })
  }

  rejectClient() {
    this.clientConfirmed = false
    this.pendingClient = null

    // Limpiar panel de confirmación
    this.confirmationPanelTarget.style.display = "none"
    this.confirmationPanelTarget.innerHTML = ""

    // Mostrar sección de búsqueda para buscar otro CUIT
    this.searchSectionTarget.style.display = "block"
    this.cuitInputTarget.focus()

    // Actualizar status message
    this.statusMessageTarget.innerHTML = `<i class="fas fa-exclamation-triangle text-warning"></i> Cliente rechazado. Busque otro CUIT o vuelva a buscar.`

    // Despachar evento
    this.dispatch("rejected")
  }

  isConfirmed() {
    return this.clientConfirmed
  }

  showSearchError(message) {
    this.searchResultTarget.innerHTML = `
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-circle"></i> ${message}
      </div>
    `
    this.searchResultTarget.style.display = "block"
  }

  showAddCuitOption(cuit, colppyClientId, foundInColppy) {
    this.searchResultTarget.innerHTML = `
      <div class="alert alert-warning">
        <i class="fas fa-exclamation-triangle"></i> Cliente no encontrado en Colppy<br>
        <small>Puede crear el cliente en Colppy ahora o agregar el CUIT de todas formas y crearlo más tarde.</small>
        <div class="mt-2">
          <button type="button" class="btn btn-sm btn-primary" data-action="click->cuit-selector#showCreateClientForm" data-cuit="${cuit}">
            <i class="fas fa-plus"></i> Crear Cliente en Colppy
          </button>
          <button type="button" class="btn btn-sm btn-warning ml-2" data-action="click->cuit-selector#addCuit" data-cuit="${cuit}" data-client-id="" data-client-name="">
            <i class="fas fa-plus"></i> Agregar CUIT de todas formas
          </button>
        </div>
      </div>
    `
    this.searchResultTarget.style.display = "block"
  }

  showCreateClientForm(event) {
    const cuit = event.currentTarget.dataset.cuit
    // Mostrar formulario modal para crear cliente
    // Por ahora, redirigir a crear cliente con el CUIT
    const formHtml = `
      <div class="modal fade" id="createClientModal" tabindex="-1" role="dialog">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Crear Cliente en Colppy</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <p>Para crear el cliente en Colppy, necesita los siguientes datos:</p>
              <form id="createClientForm">
                <div class="form-group">
                  <label>Razón Social *</label>
                  <input type="text" class="form-control" name="razon_social" required>
                </div>
                <div class="form-group">
                  <label>Nombre Fantasía</label>
                  <input type="text" class="form-control" name="nombre_fantasia">
                </div>
                <div class="form-group">
                  <label>CUIT *</label>
                  <input type="text" class="form-control" name="cuit" value="${cuit}" required>
                </div>
                <div class="form-group">
                  <label>Email</label>
                  <input type="email" class="form-control" name="email">
                </div>
                <div class="form-group">
                  <label>Condición IVA *</label>
                  <select class="form-control" name="id_condicion_iva" required>
                    <option value="1">Responsable Inscripto</option>
                    <option value="2">Exento</option>
                    <option value="3" selected>Consumidor Final</option>
                    <option value="4">Monotributista</option>
                  </select>
                </div>
              </form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancelar</button>
              <button type="button" class="btn btn-primary" data-action="click->cuit-selector#createClientInColppy" data-cuit="${cuit}">
                Crear Cliente
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    // Remover modal existente si hay
    const existingModal = document.getElementById('createClientModal')
    if (existingModal) {
      existingModal.remove()
    }

    // Agregar modal al body
    document.body.insertAdjacentHTML('beforeend', formHtml)

    // Mostrar modal
    $('#createClientModal').modal('show')
  }

  async createClientInColppy(event) {
    const cuit = event.currentTarget.dataset.cuit
    const form = document.getElementById('createClientForm')
    const formData = new FormData(form)

    const clientData = {
      cuit: formData.get('cuit'),
      razon_social: formData.get('razon_social'),
      nombre_fantasia: formData.get('nombre_fantasia') || formData.get('razon_social'),
      email: formData.get('email') || '',
      id_condicion_iva: formData.get('id_condicion_iva')
    }

    // Deshabilitar botón
    event.currentTarget.disabled = true
    event.currentTarget.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Creando...'

    try {
      const response = await fetch(`/patients/${this.patientIdValue}/invoices_and_payments/create_colppy_client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ client: clientData })
      })

      const data = await response.json()

      if (data.success && data.client_id) {
        // Cerrar modal
        $('#createClientModal').modal('hide')

        // Auto-confirmar: el operador acaba de crear el cliente manualmente, no necesita confirmar de nuevo
        this.clientConfirmed = true

        // Agregar el CUIT al selector con el nuevo cliente (esto también lo guardará)
        await this.addCuitToSelector(clientData.cuit, data.client_id, clientData.razon_social, true)
        this.updateStatusMessage(data.client_id, true)

        // Ocultar panel de confirmación si estaba visible
        this.confirmationPanelTarget.style.display = "none"
        this.confirmationPanelTarget.innerHTML = ""
        this.pendingClient = null

        // Despachar evento de confirmación
        this.dispatch("confirmed", { detail: { clientId: data.client_id, cuit: clientData.cuit } })
      } else {
        alert(data.error || "Error al crear el cliente en Colppy")
      }
    } catch (error) {
      console.error("Error creando cliente en Colppy:", error)
      alert("Error al crear el cliente en Colppy. Por favor intente nuevamente.")
    } finally {
      event.currentTarget.disabled = false
      event.currentTarget.innerHTML = 'Crear Cliente'
    }
  }

  addCuit(event) {
    const cuit = event.currentTarget.dataset.cuit
    const clientId = event.currentTarget.dataset.clientId || null
    const clientName = event.currentTarget.dataset.clientName || ""

    this.addCuitToSelector(cuit, clientId, clientName, true)
  }

  async addCuitToSelector(cuit, colppyClientId, clientName, selectIt = false) {
    // Verificar si el CUIT ya existe
    const existingOption = Array.from(this.cuitSelectTarget.options).find(opt => opt.value === cuit)
    if (existingOption) {
      // Actualizar el colppy_client_id si viene uno nuevo
      if (colppyClientId) {
        existingOption.dataset.colppyClientId = colppyClientId
      }
      // Si ya existe, seleccionarlo
      this.cuitSelectTarget.value = cuit
      this.cuitSelected()
      return
    }

    // Agregar nueva opción
    const option = document.createElement("option")
    option.value = cuit
    option.textContent = `${cuit}${clientName ? ` - ${clientName}` : ""}`
    option.dataset.colppyClientId = colppyClientId || ""

    this.cuitSelectTarget.appendChild(option)

    // Si debe seleccionarse, hacerlo
    if (selectIt) {
      this.cuitSelectTarget.value = cuit
      this.cuitSelected()
    }

    // Guardar en la lista de CUITs disponibles
    this.availableCuits.push({
      cuit: cuit,
      name: clientName || "Cliente",
      colppyClientId: colppyClientId,
      foundInColppy: !!colppyClientId
    })

    // Guardar el CUIT en la base de datos si no es el CUIT del paciente
    if (cuit !== this.initialCuitValue) {
      await this.saveBillingCuit(cuit, colppyClientId, clientName)
    }

    // Limpiar el campo de búsqueda y ocultar la sección
    this.cuitInputTarget.value = ""
    this.searchResultTarget.style.display = "none"
    this.searchSectionTarget.style.display = "none"
  }

  async saveBillingCuit(cuit, colppyClientId, clientName) {
    try {
      const response = await fetch(`/patients/${this.patientIdValue}/invoices_and_payments/save_billing_cuit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          cuit: cuit,
          colppy_client_id: colppyClientId || null,
          razon_social: clientName || null,
          nombre_fantasia: clientName || null
        })
      })

      const data = await response.json()

      if (data.success) {
        // Actualizar la opción con el ID guardado si viene en la respuesta
        const option = Array.from(this.cuitSelectTarget.options).find(opt => opt.value === cuit)
        if (option && data.billing_cuit) {
          option.dataset.colppyClientId = data.billing_cuit.colppy_client_id || colppyClientId || ""
        }
      } else {
        console.warn("No se pudo guardar el CUIT:", data.error)
        // No bloquear el flujo si falla el guardado
      }
    } catch (error) {
      console.error("Error guardando CUIT:", error)
      // No bloquear el flujo si falla el guardado
    }
  }

  cuitSelected() {
    const selectedCuit = this.cuitSelectTarget.value
    const selectedOption = this.cuitSelectTarget.options[this.cuitSelectTarget.selectedIndex]
    const colppyClientId = selectedOption?.dataset.colppyClientId

    if (colppyClientId) {
      // El CUIT ya tiene un colppy_client_id guardado (fue verificado previamente), auto-confirmar
      this.clientConfirmed = true
      this.pendingClient = null
      this.confirmationPanelTarget.style.display = "none"
      this.confirmationPanelTarget.innerHTML = ""
      this.updateStatusMessage(colppyClientId, true)
      this.dispatch("confirmed", { detail: { clientId: colppyClientId, cuit: selectedCuit } })
    } else {
      // Sin colppy_client_id, no está confirmado
      this.clientConfirmed = false
      this.updateStatusMessage(null, false, selectedCuit)
      this.dispatch("rejected")
    }
  }

  updateStatusMessage(colppyClientId, found, cuit = null) {
    if (found && colppyClientId) {
      this.statusMessageTarget.innerHTML = `<i class="fas fa-check-circle text-success"></i> Cliente confirmado en Colppy (ID: ${colppyClientId})`
      this.statusMessageTarget.className = "form-text text-muted"
    } else {
      const displayCuit = cuit || this.cuitSelectTarget.value
      if (displayCuit) {
        this.statusMessageTarget.innerHTML = `<i class="fas fa-exclamation-triangle text-warning"></i> Cliente con CUIT: ${displayCuit} - Se buscará en Colppy al facturar`
        this.statusMessageTarget.className = "form-text text-muted"
      } else {
        this.statusMessageTarget.innerHTML = `<i class="fas fa-exclamation-circle text-danger"></i> El paciente debe tener CUIT para facturar en Colppy`
        this.statusMessageTarget.className = "form-text text-muted"
      }
    }
  }
}
